import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Factory, History, User, X } from "lucide-react";
import { toast } from "react-toastify";
import { assignProductionSource, fetchOrderProductionHistory } from "../../api/OrderAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";
import { PRODUCTION_SOURCES } from "../../utils/productionSource";

const formatHistoryTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Cairo",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")} ${get("month")} ${get("hour")}:${get("minute")}`;
};

const actorLabel = (value) => {
  if (!value || value.toLowerCase() === "system") return "System";
  return value;
};

export const ProductionHistoryTimeline = ({ orderId, hideTitle = false, onAssigned }) => {
  const { authState } = useAuth();
  const canAssign = canMutateOrders(authState.role);
  const queryClient = useQueryClient();
  const [busySource, setBusySource] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["orderProductionHistory", orderId],
    queryFn: () => fetchOrderProductionHistory(orderId),
    enabled: !!orderId,
  });

  const entries = useMemo(
    () => [...(data?.entries || data?.Entries || [])],
    [data]
  );
  const current = data?.productionSource || data?.ProductionSource || null;

  const routeTo = async (source) => {
    if (!canAssign || busySource) return;
    if (!window.confirm(`تعيين مصدر الإنتاج إلى ${source} للطلب #${orderId}؟\nلن تتغير حالة الطلب.`)) return;
    try {
      setBusySource(source);
      const result = await assignProductionSource(orderId, source, `Manual route to ${source}`);
      await queryClient.invalidateQueries({ queryKey: ["orderProductionHistory", orderId] });
      onAssigned?.(result?.productionSource || result?.ProductionSource || source);
      toast.success(`Production: ${source}`);
    } catch (err) {
      toast.error(err.message || "فشل تعيين مصدر الإنتاج");
    } finally {
      setBusySource(null);
    }
  };

  if (!orderId) return null;

  return (
    <div className="text-right" dir="rtl">
      {!hideTitle && (
        <div className="font-bold text-slate-900 flex items-center gap-1.5 mb-3">
          <Factory size={16} />
          Production History
        </div>
      )}
      {current && (
        <div className="mb-3 text-sm font-bold text-slate-800">
          الحالي: {current}
        </div>
      )}
      {canAssign && (
        <div className="flex flex-wrap gap-2 mb-4">
          {PRODUCTION_SOURCES.map((source) => (
            <button
              key={source}
              type="button"
              disabled={!!busySource}
              onClick={() => routeTo(source)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              {busySource === source ? "..." : source}
            </button>
          ))}
        </div>
      )}
      {isLoading && <div className="text-sm text-slate-600">جاري تحميل سجل الإنتاج...</div>}
      {error && (
        <div className="text-sm text-red-600">
          {error.message || "فشل تحميل سجل الإنتاج"}
        </div>
      )}
      {!isLoading && !error && entries.length === 0 && (
        <div className="text-sm text-slate-500">
          لا يوجد توجيه إنتاج بعد.
        </div>
      )}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.id || entry.Id}
              className="relative pr-5 border-r-2 border-emerald-200 pb-2"
            >
              <div className="absolute right-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-600" />
              <div className="font-semibold text-gray-800 text-sm">
                {entry.source || entry.Source || "—"}
              </div>
              {(entry.previousSource || entry.PreviousSource) && (
                <div className="text-[12px] text-gray-500">
                  من: {entry.previousSource || entry.PreviousSource}
                </div>
              )}
              {(entry.reason || entry.Reason) && (
                <div className="text-[12px] text-amber-800 mt-0.5">
                  Reason: {entry.reason || entry.Reason}
                </div>
              )}
              <div className="text-[12px] text-gray-500 mt-0.5">
                {formatHistoryTime(entry.changedAt || entry.ChangedAt)}
              </div>
              <div className="text-[12px] text-emerald-700 mt-0.5 flex items-center gap-1">
                <User size={12} />
                {actorLabel(entry.changedBy || entry.ChangedBy)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HistoryModal = ({ orderId, onClose, onAssigned }) => {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Production History"
      >
        <div className="bg-slate-800 text-white px-4 py-3 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-300">Production History</p>
            <p className="font-bold text-lg truncate">طلب #{orderId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 transition shrink-0"
            aria-label="إغلاق"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <ProductionHistoryTimeline
            orderId={orderId}
            hideTitle
            onAssigned={onAssigned}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

const ProductionHistoryButton = ({
  orderId,
  className = "",
  compact = false,
  onAssigned,
}) => {
  const [open, setOpen] = useState(false);

  if (!orderId) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          (compact
            ? "w-full bg-emerald-50 text-emerald-800 border border-emerald-200 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition hover:bg-emerald-100"
            : "w-full bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition hover:bg-emerald-800")
        }
        title="عرض سجل الإنتاج"
      >
        <History size={compact ? 12 : 14} />
        Production History
      </button>
      {open ? (
        <HistoryModal
          orderId={orderId}
          onClose={() => setOpen(false)}
          onAssigned={onAssigned}
        />
      ) : null}
    </>
  );
};

export default ProductionHistoryButton;
